/**
 * Who may sign or download the agency BAA (Phase 5, ACC-03): an owner or an admin.
 * Cosmetic only; the backend enforces the same rule.
 */

import type { AuthUser, PermissionString } from '../types/permissions';

const BAA_SIGNER_ROLES: ReadonlySet<string> = new Set(['owner', 'admin']);

export function isOwner(user: AuthUser | null | undefined): boolean {
  return user?.permissions?.includes('admin:owner' as PermissionString) ?? false;
}

/** True for an owner or admin (owner permission, Cognito group, or primary role). */
export function canSignBaa(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  if (isOwner(user)) return true;
  if ((user.groups || []).some((group) => BAA_SIGNER_ROLES.has(String(group).toLowerCase()))) return true;
  return BAA_SIGNER_ROLES.has(String(user.baaSignerRole || '').toLowerCase());
}

/**
 * True for a user who may see the agency setup checklist: an owner/admin (see canSignBaa) or
 * anyone holding admin:agencies:update. Cosmetic only; the setup-status endpoint returns 403 otherwise.
 */
export function canManageAgencySetup(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  return canSignBaa(user) || (user.permissions?.includes('admin:agencies:update' as PermissionString) ?? false);
}

/** True when the tenant has a usable BAA (current version, or an older one inside its grace window). */
export function hasUsableBaa(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  if (user.baaStatus) return user.baaStatus === 'accepted' || user.baaStatus === 'grace';
  return !!user.baaAccepted;
}

/** The tenant's BAA status, derived from baaAccepted when the backend omits baaStatus. */
export function effectiveBaaStatus(user: AuthUser): NonNullable<AuthUser['baaStatus']> {
  return user.baaStatus ?? (user.baaAccepted ? 'accepted' : 'none');
}

/** True when the main layout shows a BAA banner for this user (grace, or expired for non-signers). */
export function showsBaaBanner(user: AuthUser): boolean {
  const status = effectiveBaaStatus(user);
  return status === 'grace' || (status === 'expired' && !canSignBaa(user));
}
